import * as React from 'react';
import { t } from 'i18next';

import { BaseComponent } from '../../BaseComponent';
import { LayoutType } from '../../../constants/LayoutConstants';
import { FloorPlanWizardStore } from './FloorPlanInternalStore';
import { FloorPlanSetSize } from './FloorPlanSetSize';
import { ProjectSettingsStore } from '../../../stores/ProjectSettingsStore';
import { UIHelper } from '../../../utils/UIHelper';
import { FloorPlanActions } from '../../../actions/FloorPlanActions';
import { GlobalHelper } from '../../../utils/GlobalHelper';
import { ViewSettingsActions } from '../../../actions/ViewSettingsActions';
import { VisibilityActions } from '../../../actions/VisibilityActions';
import { Overlay, PopupMenu } from '../../../constants/Enums';
import { Stats } from '../../../utils/Stats';
import { AppConfigurationStore } from '../../../stores/AppConfigurationStore';
import { SidebarHeader } from '../../SidebarHeader';
import { PersistentStore } from '../../../stores/PersistentStore';
import { FloorPlanCrop } from './FloorPlanCrop';
import { SliderDots } from '../../SliderDots';
import { OverlayCloseButton } from '../OverlayCloseButton';

import utils = egr.wcf.utils;

interface FloorPlanWizardProps extends React.Props<FloorPlanWizard> {
    layoutType: LayoutType;
}

interface FloorPlanWizardState {
    index: number;
}

export class FloorPlanWizard extends BaseComponent<FloorPlanWizardProps, FloorPlanWizardState> {
    private internalStore: FloorPlanWizardStore = new FloorPlanWizardStore();
    private setSizeComponent: FloorPlanSetSize;

    public componentWillMount(): void {
        this.setState({index: 0});
    }

    public render(): JSX.Element {
        this.sendPageView();

        const headerLabels: Array<string> = [
            t('Scale floor plan'),
            t('Scale floor plan'),
            t('Crop')
        ];
        const descriptionLabels: Array<string> = [
            t('Select the start and end points for a known distance'),
            t('Enter length'),
            t('Select image area')
        ];
        return (
            <div className={'overlay-frame' + (AppConfigurationStore.getState().inApp ? ' overlay-frame-with-header' : '')}>
                <canvas id="fp-loupe" className="fp-loupe" width="80" height="80"/>
                {AppConfigurationStore.getState().inApp ? (
                    <SidebarHeader
                        text={headerLabels[this.state.index]}
                        link={PersistentStore.getState().language === 'de' ? 'https://youtu.be/MLQs8qlZWwA' : 'https://youtu.be/srIMa1BMAK8'}
                        linkClassName={'questionmark-link' + (this.props.layoutType === LayoutType.SMALL ? '' : ' questionmark-link-right')}
                    >
                        {
                            this.props.layoutType !== LayoutType.SMALL ?
                            (
                                <div
                                    className="overlay-close-label"
                                    onClick={() => { VisibilityActions.changeOverlay(Overlay.NONE); }}
                                >
                                    <span>{t('Close')}</span>
                                </div>
                            ) :
                            <OverlayCloseButton show={AppConfigurationStore.getState().inApp}/>
                        }
                    </SidebarHeader>
                ) : null}
                <div className="overlay-content overlay-content-resized">
                    <OverlayCloseButton show={!AppConfigurationStore.getState().inApp}/>
                    {AppConfigurationStore.getState().inApp ? null : (
                        <div className="overlay-content-header overlay-content-header-extra">
                            <span>{headerLabels[this.state.index]}</span>
                            <a
                                href={PersistentStore.getState().language === 'de' ? 'https://youtu.be/MLQs8qlZWwA' : 'https://youtu.be/srIMa1BMAK8'}
                                className="questionmark-link questionmark-link-right"
                                target="_blank"
                            />
                        </div>
                    )}
                    <div className="overlay-content-main">
                        <FloorPlanSetSize
                            key="size-canvas"
                            layoutType={this.props.layoutType}
                            pageIndex={this.state.index}
                            ref={(node: FloorPlanSetSize) => { this.setSizeComponent = node; }}
                            store={this.internalStore}
                            submitCallback={this.goToFinalPage}
                        />
                        <FloorPlanCrop
                            layoutType={this.props.layoutType}
                            pageIndex={this.state.index}
                            store={this.internalStore}
                        />
                    </div>
                    <div className="overlay-content-footer">{descriptionLabels[this.state.index]}</div>
                    {this.prevButton()}
                    <SliderDots list={[0, 1, 2]} item={this.state.index} max={0}/>
                    {this.nextButton()}
                </div>
            </div>
        );
    }

    protected goToFinalPage = (): void => {
        const cleanUpValue: number = parseFloat(this.setSizeComponent.inputBox.input.value.replace(',', '.'));
        const convertedValue: number = utils.number.convertLengthUnit(
            cleanUpValue,
            ProjectSettingsStore.getLengthUnit(),
            utils.number.LengthUnit.Meter
        );
        if (isNaN(convertedValue) || convertedValue <= 0) {
            UIHelper.showMessage(t('Please enter a value!'), () => {
                this.setSizeComponent.inputBox.input.value = '';
                this.setSizeComponent.inputBox.focus();
            });
        } else {
            this.internalStore.scaleDistance = cleanUpValue;
            const ratio: number = (this.internalStore.pixelDistance / convertedValue);
            FloorPlanActions.setAngle(this.internalStore.getFixedAngle());
            FloorPlanActions.setRatio(ratio);
            this.setState({index: 2});
        }
    };

    protected nextPage = (): void => {
        switch (this.state.index) {
            case 0:
                this.setState({index: 1});
                break;
            case 1:
                this.goToFinalPage();
                break;
            default:
                break;
        }
    };

    protected prevPage = (): void => {
        if (this.state.index > 0) {
            this.setState({index: this.state.index - 1});
        }
    };

    protected getCroppedImage = (): void => {
        if (!this.internalStore.validSize()) {
            const sizeInfo: string | null = GlobalHelper.getLocaleLengthString(
                utils.number.convertLengthUnit(50, utils.number.LengthUnit.Meter, ProjectSettingsStore.getLengthUnit())
            );
            UIHelper.showMessage(
                t(
                    'Please select a new image area! The area must be smaller than {{size}}.',
                    {size: sizeInfo + ' x ' + sizeInfo }
                )
            );
        } else {
            const imageElement: HTMLImageElement = document.getElementById('fp-image') as HTMLImageElement;
            ViewSettingsActions.withLock<void>(
                () => {
                    return new Promise<void>(async (resolve, reject) => {
                        await UIHelper.timeout(500);
                        FloorPlanActions.setFloorPlan(
                            imageElement,
                            this.internalStore.cropImagePosition,
                            this.internalStore.cropPosition,
                            this.internalStore.cropZoom,
                            this.internalStore.getRotation(true)
                        ).then(async (): Promise<void> => {
                            await UIHelper.timeout(500);
                            VisibilityActions.changeOverlay(Overlay.NONE);
                            imageElement.src = '#';

                            Stats.sendEvent({
                                category: 'FloorPlan',
                                action: 'finished'
                            });
                            VisibilityActions.setPopupMenu(PopupMenu.FLOORPLAN_SETUP);
                            resolve();
                        });
                    });
                }
            );
        }
    };

    protected prevButton(): JSX.Element {
        switch (this.state.index) {
            case 0:
                return (
                    <div className="overlay-left-button colored-font" onClick={FloorPlanActions.getImageFile}>{t('Change image')}</div>
                );
            default:
                return (
                    <div className="overlay-left-button colored-font" onClick={this.prevPage}>{t('Back')}</div>
                );
        }
    }

    protected nextButton(): JSX.Element {
        switch (this.state.index) {
            case 2:
                return (
                    <div className="overlay-right-button colored-font" onClick={() => {this.getCroppedImage(); }}>{t('Done')}</div>
                );
            default:
                return (
                    <div className="overlay-right-button colored-font" onClick={this.nextPage}>{t('Next')}</div>
                );
        }
    }

    protected sendPageView(): void {
        switch (this.state.index) {
            case 0:
                Stats.sendPageView('floorplan', ['distance']);
                break;
            case 1:
                Stats.sendPageView('floorplan', ['length']);
                break;
            case 2:
                Stats.sendPageView('floorplan', ['crop']);
                break;
            default:
                break;
        }
    }
}


// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/components/overlay/floorplan/FloorPlanWizard.tsx